import * as Sentry from '@sentry/react';
import { Map } from 'immutable';

import { setUserLoaded, shapeBetaAccessForApp, shapeSubscriptionsForApp, shapeUserDataForApp } from '@/utils/user';
import { getFromLocalStorage, saveToLocalStorage } from '@/utils/localStorage';
import { isVIPPlan } from '@/utils/billing';
import { posthog } from '@/utils/tracking/posthog';
import { api } from '@/utils/api';

import { VELA_THEME, VIP_INTRO_SEEN } from '@/constants/user';
import { MESSAGE, NOTIFICATION } from '@/constants/notifications';
import { EMPTY, FALSE } from '@/constants';
import { SECOND } from '@/constants/time';

import actions from '@/actions';

function* changeTheme(reduction, { theme }) {
  yield function effect(dispatch) {
    const oldTheme = reduction.getIn(['user', VELA_THEME]);

    api.user.setProfileValue({ [VELA_THEME]: theme })
      .then(
        () => dispatch(actions.user.changeTheme.fulfilled({ theme })),
        (error) => dispatch(actions.user.changeTheme.rejected({ error, oldTheme })),
      );
  };

  return reduction
    .deleteIn(['user', 'loaded'])
    .setIn(['user', 'loading'], true)
    .setIn(['user', VELA_THEME], theme);
}

function* changeThemeFulfilled(reduction, { theme }) {
  yield function effect() {
    saveToLocalStorage({ [VELA_THEME]: theme });
  };

  return reduction
    .deleteIn(['user', 'loading'])
    .setIn(['user', 'loaded'], true);
}

function* changeThemeRejected(reduction, { error, oldTheme }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.CHANGE_THEME,
      })
    );
  };

  return reduction
    .deleteIn(['user', 'loading'])
    .setIn(['user', 'loaded'], true)
    .setIn(['user', VELA_THEME], oldTheme);
}

function* getBetaAccess(reduction) {
  yield function effect(dispatch) {
    api.user.betaAccess.getSubscriptions()
      .then(
        (response) => dispatch(actions.user.getBetaAccess.fulfilled(response)),
        (error) => dispatch(actions.user.getBetaAccess.rejected(error)),
      );
  };

  return reduction;

}

function* getBetaAccessFulfilled(reduction, response) {
  return reduction.setIn(['user', 'betaAccess'], shapeBetaAccessForApp(response));
}

function* getBetaAccessRejected(reduction, error) {
  yield function effect() {
    console.error(error);
  };

  return reduction;
}

function* getConfig(reduction) {
  yield function effect(dispatch) {
    api.user.getConfig()
      .then(
        (config) => dispatch(actions.user.getConfig.fulfilled(config)),
        (error) => dispatch(actions.user.getConfig.rejected(error)),
      );
  };

  return reduction;
}

function* getConfigFulfilled(reduction, config) {
  return reduction.setIn(['user', 'config'], Map(config));
}

function* getConfigRejected(reduction, error) {
  yield function effect() {
    console.error(error);
  };

  return reduction;
}

function* getSubscriptions(reduction) {
  yield function effect(dispatch) {
    api.billing.getSubscriptions()
      .then(
        (response) => dispatch(actions.user.getSubscriptions.fulfilled(response)),
        (error) => dispatch(actions.user.getSubscriptions.rejected(error)),
      );
  };

  return reduction;
}

function* getSubscriptionsFulfilled(reduction, response) {
  const user = reduction.get('user');
  const state = reduction.setIn(['user', 'subscriptions'], shapeSubscriptionsForApp({ subscriptions: response, user }));

  yield function effect(dispatch) {
    function reduceSubscriptions(result, subscription) {
      if (
        result === false &&
        !subscription.get('tierChangeDetected') &&
        isVIPPlan(subscription.get('plan'))
      ) {
        dispatch(actions.user.setProfileValue({ name: VIP_INTRO_SEEN, save: false, value: FALSE }));
        return true;
      }

      return result;
    }

    state
      .getIn(['user', 'subscriptions'])
      .reduce(reduceSubscriptions, reduction.hasIn(['user', VIP_INTRO_SEEN]) ? undefined : false);
  };

  return setUserLoaded(state);
}

function* getSubscriptionsRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.ERROR,
        keep: true,
      })
    );
  };

  return setUserLoaded(reduction);
}

function* getUserData(reduction) {
  yield function effect(dispatch) {
    api.user.getUserData()
      .then(
        (response) => dispatch(actions.user.getUserData.fulfilled(response)),
        (error) => dispatch(actions.user.getUserData.rejected(error))
      );
  };

  return reduction.setIn(['user', 'loading'], true);
}

function* getUserDataFulfilled(reduction, response) {
  if (!reduction.getIn(['user', 'loading'])) return reduction;

  let user = shapeUserDataForApp({
    data: response,
    user: reduction
      .get('user', EMPTY.MAP)
      .delete('loading'),
  });

  if (!user.get(VELA_THEME)) {
    const theme = reduction.getIn(['user', VELA_THEME]);
    user = user.set(VELA_THEME, theme);

    yield function effect() {
      api.user.setProfileValue({ [VELA_THEME]: theme });
    };
  }

  if (getFromLocalStorage({ [VELA_THEME]: undefined })[VELA_THEME] !== user.get(VELA_THEME)) {
    saveToLocalStorage({ [VELA_THEME]: user.get(VELA_THEME) });
  }

  yield function effect(dispatch) {
    dispatch(actions.user.getConfig());
    dispatch(actions.user.getBetaAccess());
    dispatch(actions.user.getUserProfile());
    dispatch(actions.user.getSubscriptions());
  };

  return setUserLoaded(reduction.setIn(['auth', 'loggedIn'], true).set('user', user));
}

function* getUserDataRejected(reduction) {
  yield function effect(dispatch) {
    dispatch(actions.user.getConfig());
  };

  return reduction
    .deleteIn(['user', 'loading'])
    .setIn(['auth', 'loggedIn'], false);
}

function* getUserProfile(reduction) {
  yield function effect(dispatch) {
    api.user.getJWTData()
      .then(
        (response) => dispatch(actions.user.getUserProfile.fulfilled(response)),
        (error) => dispatch(actions.user.getUserProfile.rejected(error)),
      );
  };

  return reduction;
}

function* getUserProfileFulfilled(reduction, data) {
  const { avatar, createdAt, email, firstName, lastName, userHash, userId, userName } = data;

  if (email !== reduction.getIn(['user', 'profile', 'email'])) {
    yield function effect() {
      Sentry.setTag('user_id', data.userId);
      posthog.setUser(data);

      if ('Intercom' in window) {
        window.Intercom(
          'update',
          {
            alignment: 'left',
            created_at: Math.floor(new Date(createdAt).getTime() / SECOND),
            email: userName,
            hide_default_launcher: true,
            horizontal_padding: 21,
            user_hash: userHash,
            user_id: userId,
            vertical_padding: 100,
          }
        );
      }
    };
  }

  return reduction.setIn(['user', 'profile'], Map({ avatar, email, firstName, lastName }));
}

function* getUserProfileRejected(reduction, error) {
  yield function effect() {
    console.error(error);
  };

  return reduction;
}

function* setProfileValue(reduction, { name, save = true, value }) {
  if (typeof value !== 'string') return reduction;

  if (save) {
    yield function effect() {
      api.user.setProfileValue({ [name]: value });
    };
  }

  return reduction.setIn(['user', name], value);
}

const user = {
  [actions.user.changeTheme]: changeTheme,
  [actions.user.changeTheme.fulfilled]: changeThemeFulfilled,
  [actions.user.changeTheme.rejected]: changeThemeRejected,
  [actions.user.getBetaAccess]: getBetaAccess,
  [actions.user.getBetaAccess.fulfilled]: getBetaAccessFulfilled,
  [actions.user.getBetaAccess.rejected]: getBetaAccessRejected,
  [actions.user.getConfig]: getConfig,
  [actions.user.getConfig.fulfilled]: getConfigFulfilled,
  [actions.user.getConfig.rejected]: getConfigRejected,
  [actions.user.getSubscriptions]: getSubscriptions,
  [actions.user.getSubscriptions.fulfilled]: getSubscriptionsFulfilled,
  [actions.user.getSubscriptions.rejected]: getSubscriptionsRejected,
  [actions.user.getUserData]: getUserData,
  [actions.user.getUserData.fulfilled]: getUserDataFulfilled,
  [actions.user.getUserData.rejected]: getUserDataRejected,
  [actions.user.getUserProfile]: getUserProfile,
  [actions.user.getUserProfile.fulfilled]: getUserProfileFulfilled,
  [actions.user.getUserProfile.rejected]: getUserProfileRejected,
  [actions.user.setProfileValue]: setProfileValue,
};

export default user;
